<template>
  <div>
    <h3 class="mt-4 d-flex" v-if="!field.hideSectionTitle">
      {{ sectionTitle }}
    </h3>
    <hr style="margin: 0" v-if="!field.hideSectionTitle" />

    <div class="description mt-2" v-if="description && description !== ''">
      <div v-html="description"></div>
    </div>
    <EditButtonSetting
      :modelKey="buttonModelKey"
      :defaultValue="`${getLabelByKeys('BUTTONS.ADD')} ${convertToSingularForm(
        header
      )}`"
      classList="mylisting-button dark-button mt-3 mb-3"
      @click="showAddObjectDialog()"
    />
    <button
      v-if="field.downloadFileURL"
      class="mylisting-button secondary-button mt-3 mb-3"
      @click="
        downloadFile(
          field.downloadFileURL,
          field.downloadFileText
            ? field.downloadFileText
            : 'Download Reference Check File'
        )
      "
    >
      {{
        field.downloadFileText
          ? field.downloadFileText
          : "Download Reference Check File"
      }}
    </button>
    <div v-for="(object, index) of data" :key="'ref' + index">
      <hr class="ref-separator" />
      <h5 style="font-weight: 600; margin-bottom: 0">
        {{ getObjectTitle(object) }}
        <span class="edit-button" @click="showEditObjectDialog(object, index)">
          <i class="material-icons"> edit </i>
        </span>
      </h5>
      <DetailSectionRowLayout
        :modelData="object"
        :listFields="listFieldsNormalized"
        :listExcludeFields="keyTitles"
      />
    </div>
    <p
      class="required-message"
      v-if="
        minCount &&
        data.length < minCount &&
        (submitClicked || showMinEntriesRequiredApplicationForm)
      "
    >
    You need at least {{ minCount }} {{ minCount === 1? convertToSingularForm(header): header }}!
    </p>
    <ProfileSectionDialog
      :name="name"
      :header="header"
      :listFields="listFieldsNormalized"
      :keyTitles="keyTitles"
      v-model="isShowObjectFormDialog"
      :data="object"
      :field="field"
      :profileBuilderID="profileBuilderID"
      :isAdd="isAddObjectDialog"
      @saved="handleUpdateObjects($event)"
      :submitInfo="fileSubmitInfo"
    />
  </div>
</template>

<script>
import DetailSectionRowLayout from "./DetailSectionRowLayout";
import ProfileSectionDialog from "./ProfileSectionDialog";
import { ACTION_TYPE } from "../../constants/custom-define-type";
import { mapGetters, mapActions } from "vuex";
/**
 * The component is used to show for the section like reference or experience or education
 * in the application form
 */
export default {
  props: {
    header: String,
    sectionTitle: String,
    // Description which are only visible to admin
    adminDescription: String,
    description: String,
    name: String,
    listFields: Array,
    keyTitles: {
      type: Array,
      default: () => ["name"],
    },
    // List models which is only showed when access from admin account
    listModelsAdminOnly: {
      type: Array,
      default: () => [],
    },
    // List models which is only visible for admin and client
    listModelsAdminClient: {
      type: Array,
      default: () => [],
    },
    data: Array,
    submitClicked: {
      type: Boolean,
      default: false,
    },
    // Minimum number of item
    minCount: Number,
    field: {
      type: Object,
      default: () => {},
    },
    profileBuilderID: Number,
    applicationData: {
      type: Object,
      default: () => {},
    },
  },
  components: {
    DetailSectionRowLayout,
    ProfileSectionDialog,
  },
  data() {
    return {
      isShowObjectFormDialog: false,
      object: {},
      isAddObjectDialog: false,
      indexOfEdittingObject: 0,
      isShowSectionBuilderUpSertDialog: false,
    };
  },
  computed: {
    ...mapGetters([
      "language",
      "showMinEntriesRequiredApplicationForm",
      "isAgent",
    ]),
    buttonModelKey() {
      return `section_${this.profileBuilderID}_${
        this.field ? this.field.sectionBuilderId : ""
      }_add_text_${this.language || ""}`;
    },
    listFieldsNormalized() {
      const listExcludeModels = [
        ...this.listModelsAdminOnly,
        ...this.listModelsAdminClient,
      ];
      let result = this.listFields.filter(
        (field) =>
          !field.notInApplication && !listExcludeModels.includes(field.model)
      );
      // Check show condition of people fields
      if (this.applicationData) {
        result = result.filter((field) => {
          if (!field.showIf) {
            return true;
          }
          if (field.showIf.indexOf("people_") === 0) {
            return this.isItemShow(
              this.applicationData,
              { ...field, showIf: field.showIf.replace("people_", "") },
              []
            );
          }
          return true;
        });
        result = this.cloneArrayOfObject(result);
        result.forEach((field) => {
          if (field.showIf && field.showIf.indexOf("people_") === 0) {
            field.showIf = null;
          }
        });
      }
      return result;
    },
    listShareModelFields() {
      return this.listFieldsNormalized.filter((field) => field.isShareModel);
    },
    fileSubmitInfo() {
      if (this.profileBuilderID) {
        return {
          profileBuilder: this.profileBuilderID,
          email: this.applicationData ? this.applicationData.email : null,
        };
      }
    },
  },
  methods: {
    ...mapActions(["reloadProfileBuilderList"]),
    getObjectTitle(object) {
      return this.keyTitles.map((title) => object[title]).join(" ");
    },
    showAddObjectDialog() {
      this.isAddObjectDialog = true;
      this.object = {
        section_builder_id: this.field ? this.field.sectionBuilderId : null,
        profile_builder_id: this.profileBuilderID,
      };
      this.listShareModelFields.forEach((field) => {
        this.object[field.model] = [];
        this.object[field.model][field.shareModelIndex] = "";
      });
      this.isShowObjectFormDialog = true;
    },
    showEditObjectDialog(ref, index) {
      this.indexOfEdittingObject = index;
      this.isAddObjectDialog = false;
      this.object = {
        ...ref,
        section_builder_id: this.field ? this.field.sectionBuilderId : null,
        profile_builder_id: this.profileBuilderID,
      };
      this.isShowObjectFormDialog = true;
    },
    handleUpdateObjects({ type, data }) {
      if (type === ACTION_TYPE.ADD) {
        this.data.push({ ...data, id: 0 });
      } else if (type === ACTION_TYPE.UPDATE) {
        this.data[this.indexOfEdittingObject] = data;
      } else if (type === ACTION_TYPE.DELETE) {
        this.data.splice(this.indexOfEdittingObject, 1);
      }
    },
    handleShowBuilderSetting() {
      this.isShowSectionBuilderUpSertDialog = true;
    },
  },
};
</script>

<style scoped>
.description {
  font-size: 80%;
  font-weight: 400;
  /* margin-top: 10px; */
}
</style>



// WEBPACK FOOTER //
// src/components/common/ApplicationSectionWithDialog.vue